import React, { useState } from "react";
import { Alert, Button, Modal, Table, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { IMsalContext } from "@azure/msal-react";
import { TransferExecution, TransferSchedule } from "./types";
import {
  cancelTransfer,
  deleteSchedule,
  fetchActiveTransfers,
} from "./dataTransferApi";
import { extractDataTransferError } from "./extractError";
import { formatTimestamp, RouteCell, StatusBadge } from "./display";

const { Text } = Typography;

const PAGE_SIZE = 20;

interface ActiveTransferTabProps {
  /** Display name shown in the tab copy. */
  spaceName: string;
  /** Canonical space name sent as the `space` query param. */
  spaceApiName?: string | null;
  msalInstance: IMsalContext;
}

type Notice = { type: "success" | "error"; message: string } | null;

const ActiveTransferTab: React.FC<ActiveTransferTabProps> = ({
  spaceName,
  spaceApiName,
  msalInstance,
}) => {
  const queryClient = useQueryClient();
  const apiSpace = spaceApiName?.trim() || spaceName;

  const [notice, setNotice] = useState<Notice>(null);
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [pendingScheduleId, setPendingScheduleId] = useState<string | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  const {
    data,
    isFetching,
    isFetchingNextPage,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["dataTransfer", "active", apiSpace],
    queryFn: ({ pageParam }) =>
      fetchActiveTransfers(msalInstance, apiSpace, {
        limit: PAGE_SIZE,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.next_cursor ?? undefined,
    staleTime: 30 * 1000,
  });

  const executions = data?.pages.flatMap(page => page.items) ?? [];
  const schedules = data?.pages[0]?.active_schedules ?? [];

  const refreshLists = () => {
    queryClient.invalidateQueries({
      queryKey: ["dataTransfer", "active", apiSpace],
    });
    queryClient.invalidateQueries({
      queryKey: ["dataTransfer", "past", apiSpace],
    });
  };

  const handleConfirmCancel = async () => {
    if (!pendingCancelId) return;
    setCancelling(true);
    setNotice(null);
    try {
      const result = await cancelTransfer(
        msalInstance,
        pendingCancelId,
        apiSpace,
      );
      const id = result?.execution_id || pendingCancelId;
      setNotice({
        type: "success",
        message: `Stop requested for ${id}. Open Past Execution and select Refresh to see it as CANCELLED.`,
      });
      refreshLists();
    } catch (error: unknown) {
      setNotice({
        type: "error",
        message: extractDataTransferError(error, "Failed to cancel transfer."),
      });
    } finally {
      setCancelling(false);
      setPendingCancelId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingScheduleId) return;
    setDeleting(true);
    setNotice(null);
    try {
      const result = await deleteSchedule(
        msalInstance,
        apiSpace,
        pendingScheduleId,
      );
      const name = result?.schedule_name || pendingScheduleId;
      setNotice({
        type: "success",
        message: `Schedule ${name} deleted.`,
      });
      refreshLists();
    } catch (error: unknown) {
      setNotice({
        type: "error",
        message: extractDataTransferError(error, "Failed to delete schedule."),
      });
    } finally {
      setDeleting(false);
      setPendingScheduleId(null);
    }
  };

  const executionColumns: ColumnsType<TransferExecution> = [
    {
      title: "Route",
      key: "route",
      width: 280,
      ellipsis: true,
      render: (_, row) => (
        <RouteCell
          route={row.route}
          source={row.source_space}
          destination={row.destination_space}
          status={row.status}
        />
      ),
    },
    {
      title: "Execution ID",
      dataIndex: "execution_id",
      key: "execution_id",
      width: 180,
      ellipsis: true,
      render: (val: string) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      width: 110,
      render: (val: string) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (val: string) => <StatusBadge status={val} />,
    },
    {
      title: "Started",
      dataIndex: "started_at",
      key: "started_at",
      width: 170,
      render: (val: string | null) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {formatTimestamp(val)}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, row) => (
        <Button
          size="small"
          onClick={() => setPendingCancelId(row.execution_id)}
        >
          Cancel
        </Button>
      ),
    },
  ];

  const scheduleColumns: ColumnsType<TransferSchedule> = [
    {
      title: "Route",
      key: "route",
      width: 280,
      ellipsis: true,
      render: (_, row) => (
        <RouteCell
          route={row.route}
          source={row.source_space}
          destination={row.destination_space}
          status={row.status}
        />
      ),
    },
    {
      title: "Schedule",
      dataIndex: "schedule_expression",
      key: "schedule",
      width: 180,
      ellipsis: true,
      render: (val: string) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "End date",
      dataIndex: "end_date",
      key: "end_date",
      width: 120,
      render: (val: string) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {val || "—"}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (val: string) => <StatusBadge status={val} />,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 170,
      render: (val: string) => (
        <Text className="dt-nowrap" style={{ fontSize: 13 }}>
          {formatTimestamp(val)}
        </Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, row) => (
        <Button
          size="small"
          onClick={() => setPendingScheduleId(row.schedule_id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  const tableLoading = isFetching && !isFetchingNextPage;

  return (
    <div className="dt-tab-body">
      {notice && (
        <Alert
          type={notice.type}
          showIcon
          message={notice.message}
          closable
          onClose={() => setNotice(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {isError && (
        <Alert
          type="error"
          showIcon
          message="Failed to load active transfers."
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="dt-tab-toolbar">
        <Button
          icon={<ReloadOutlined />}
          onClick={() =>
            queryClient.resetQueries({
              queryKey: ["dataTransfer", "active", apiSpace],
            })
          }
          loading={tableLoading}
        >
          Refresh
        </Button>
      </div>

      <div className="dt-list-block">
        <Text strong className="dt-list-heading">
          Running transfers
        </Text>
        <Table<TransferExecution>
          className="dt-transfer-table"
          dataSource={executions}
          columns={executionColumns}
          rowKey={row => row.execution_id}
          size="small"
          loading={tableLoading}
          pagination={false}
          locale={{ emptyText: "No running transfers for this space." }}
        />
        {hasNextPage && (
          <div className="dt-load-more">
            <Button
              onClick={() => fetchNextPage()}
              loading={isFetchingNextPage}
            >
              Load more
            </Button>
          </div>
        )}
      </div>

      <div className="dt-list-block">
        <Text strong className="dt-list-heading">
          Schedules
        </Text>
        <Table<TransferSchedule>
          className="dt-transfer-table"
          dataSource={schedules}
          columns={scheduleColumns}
          rowKey={row => row.schedule_id}
          size="small"
          loading={tableLoading}
          pagination={false}
          locale={{ emptyText: "No live schedules for this space." }}
        />
      </div>

      <Modal
        title="Stop this execution?"
        open={!!pendingCancelId}
        onOk={handleConfirmCancel}
        onCancel={() => setPendingCancelId(null)}
        okText="Stop execution"
        okButtonProps={{ danger: true, loading: cancelling }}
        cancelText="Keep"
      >
        <Text>
          This stops the Glue execution. It shows on Past Execution as CANCELLED
          after you select Refresh there. Live schedules stay until you delete
          them.
        </Text>
      </Modal>

      <Modal
        title="Delete this schedule?"
        open={!!pendingScheduleId}
        onOk={handleConfirmDelete}
        onCancel={() => setPendingScheduleId(null)}
        okText="Delete schedule"
        okButtonProps={{ danger: true, loading: deleting }}
        cancelText="Keep"
      >
        <Text>
          This removes the live schedule. Completed runs stay in Past.
        </Text>
      </Modal>
    </div>
  );
};

export default ActiveTransferTab;
